/**
 * Centro de Reportes — 7 reportes exportables a Excel (SheetJS).
 */
import { todayISO } from '../../../lib/utils'
import { tenantLocale, tenantCurrency } from '@/lib/locale'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { usePermission } from '@/hooks/usePermission'
import { useAuthStore } from '@/store/authStore'
import DatePicker from '@/components/DatePicker'
import * as XLSX from 'xlsx'
import { formatRut } from '@/lib/utils'
import { Download, FileText, TrendingDown, ShoppingBag, AlertTriangle,
         Landmark, Package, Users, Building2, Receipt, TrendingUp, ChevronDown, ChevronRight,
         Copy, Check, FileDown, HelpCircle, CalendarRange } from 'lucide-react'
import apiClient from '@/lib/apiClient'
import { HelpPanel } from '@/components/HelpPanel'
import { GlassPanel } from '@/components/ds/GlassPanel'
import { HELP } from '@/data/helpContent'
import { useTenantTax, useLocalizationConfig } from '@/modules/config/hooks/useConfig'
import { useSupplierInvoice } from '@/modules/purchases/hooks/usePurchases'
import { SupplierInvoiceDetailModal } from '@/modules/purchases/components/SupplierInvoiceDetailModal'
import { PurchasesGroupDetail } from './PurchasesGroupDetail'
import { ContractsByMonthReport } from './ContractsByMonthReport'
import { StatusBadge, DTE_STATUS } from '@/components/ds/StatusBadge'
import {
  useSalesReport, useARAgingReport, usePurchasesReport, useAPAgingReport,
  useFactoringReport, useSalesByProductReport, useCustomerAnalysisReport,
  useCostCenterDistributionReport, useF29, useF29Details, useF22, useCommissionReport,
  type ReportParams, type ContributionSummary,
  type F29SaleRow, type F29PurchaseRow, type F29HonorariosRow, type F29DetailsType,
  type SalesReportRow, type ARAgingRow, type PurchasesReportRow, type APAgingRow,
  type FactoringReportRow, type SalesByProductRow, type CustomerAnalysisRow,
  type CostCenterDistributionRow, type CommissionReportRow, type F22BreakdownRow,
} from '../hooks/useReports'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { Pagination } from '@/components/ds/Pagination'
import { useTenantLocale } from '@/lib/locale'
import { EmptyState } from '@/components/EmptyState'
import { AGING_BUCKETS } from '@/components/ds/agingBuckets'

// ─── Default date range (current month) ──────────────────────────────────────
const _today = todayISO()
const _firstOfMonth = _today.substring(0, 7) + '-01'
const DEFAULT_PARAMS: ReportParams = { date_from: _firstOfMonth, date_to: _today }

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(value: string | number | undefined, currency = true, currencyCode = '') {
  if (value === undefined || value === null) return '—'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '—'
  if (currency) {
    // Sin `maximumFractionDigits`: Intl ya sabe cuántos decimales lleva cada
    // moneda, y forzar 0 le come los céntimos a un total en soles.
    return new Intl.NumberFormat(tenantLocale(), {
      style: 'currency', currency: currencyCode || tenantCurrency(),
    }).format(num)
  }
  return num.toLocaleString(tenantLocale())
}

function exportToExcel(data: Record<string, unknown>[], sheetName: string, filename: string) {
  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName)
  XLSX.writeFile(wb, `${filename}_${todayISO()}.xlsx`)
}

// ─── DateFilters ──────────────────────────────────────────────────────────────

function DateFilters({ params, setParams }: {
  params: ReportParams
  setParams: (p: ReportParams) => void
}) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <label htmlFor="reportscenterpage-desde" className="text-xs text-content-muted font-medium">Desde</label>
        <DatePicker id="reportscenterpage-desde" value={params.date_from || ''} onChange={v => setParams({ ...params, date_from: v || undefined })} />
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor="reportscenterpage-hasta" className="text-xs text-content-muted font-medium">Hasta</label>
        <DatePicker id="reportscenterpage-hasta" value={params.date_to || ''} onChange={v => setParams({ ...params, date_to: v || undefined })} />
      </div>
      {(params.date_from !== DEFAULT_PARAMS.date_from || params.date_to !== DEFAULT_PARAMS.date_to) && (
        <button
          onClick={() => setParams(DEFAULT_PARAMS)}
          className="text-xs text-content-subtle hover:text-content-muted transition-colors"
        >
          Restablecer mes actual
        </button>
      )}
    </div>
  )
}

// ─── SummaryCards ─────────────────────────────────────────────────────────────

function SummaryCards({ summary, currency }: { summary: Record<string, string | number>; currency: string }) {
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)
  const entries = Object.entries(summary)
  if (entries.length === 0) return null
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
      {entries.map(([key, val]) => (
        <div key={key} className="bg-glass-3 rounded-lg p-3 border border-divider">
          <p className="text-xs text-content-muted capitalize">{key.replace(/_/g, ' ')}</p>
          <p className="text-base font-semibold text-content mt-0.5">
            {typeof val === 'number' ? val.toLocaleString('es-CL') : (
              isNaN(parseFloat(val as string)) ? String(val) : fmtCur(val)
            )}
          </p>
        </div>
      ))}
    </div>
  )
}

// ─── LoadingSkeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      <div className="h-8 bg-surface-muted rounded" />
      {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-10 bg-surface-muted rounded" />)}
    </div>
  )
}

// ─── Tab: Ventas ──────────────────────────────────────────────────────────────

/*
 * Era la CUARTA copia del mapa de estados, y la más incompleta: tenía 6 de los
 * 10, así que un documento en `xml_signed`, `pending_send`, `xml_generated` o
 * `querying_status` salía con su clave cruda — en la tabla y también en el
 * Excel exportado, que es lo que alguien reenvía por correo.
 */
const rotuloDelEstado = (s: string) => DTE_STATUS[s]?.label ?? s

function SalesTab() {
  const [params, setParams] = useState<ReportParams>(DEFAULT_PARAMS)
  const tax = useTenantTax()
  const { data, isLoading, error } = useSalesReport(params)
  const currency = data?.currency ?? 'CLP'
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)

  const handleExport = () => {
    if (!data?.data) return
    exportToExcel(
      data.data.map(r => ({
        'Estado': rotuloDelEstado(r.status),
        'Tipo Documento': r.document_type,
        'Cantidad': r.count,
        'Subtotal': r.subtotal,
        [tax.name]: r.tax_amount,
        'Total': r.total,
      })),
      'Ventas', 'reporte_ventas'
    )
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <DateFilters params={params} setParams={setParams} />
        <button onClick={handleExport} disabled={!data?.data?.length}
          className="flex items-center gap-2 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover transition-colors disabled:opacity-40">
          <Download size={15} /> Exportar Excel
        </button>
      </div>
      {data?.summary && <SummaryCards summary={data.summary} currency={currency} />}
      {isLoading ? <LoadingSkeleton /> : error ? (
        <p className="text-sm text-danger-solid">Error cargando reporte</p>
      ) : (
        <DataTable<SalesReportRow>
          columns={[
            { key: 'status', header: 'Estado', sortable: true,
              sortValue: r => rotuloDelEstado(r.status),
              render: r => <StatusBadge status={r.status} catalog={DTE_STATUS} /> },
            { key: 'document_type', header: 'Tipo', sortable: true,
              render: r => <span className="font-mono text-xs">{r.document_type}</span> },
            { key: 'count', header: 'Cant.', numeric: true, sortable: true,
              sortValue: r => Number(r.count) },
            { key: 'subtotal', header: 'Subtotal', numeric: true, sortable: true,
              sortValue: r => Number(r.subtotal), render: r => fmtCur(r.subtotal) },
            { key: 'tax_amount', header: tax.name, numeric: true, sortable: true,
              sortValue: r => Number(r.tax_amount), render: r => fmtCur(r.tax_amount) },
            { key: 'total', header: 'Total', numeric: true, sortable: true,
              sortValue: r => Number(r.total),
              render: r => <span className="font-semibold">{fmtCur(r.total)}</span> },
          ]}
          rows={data?.data ?? []}
          rowKey={(_, i) => i}
          surface={false}
          empty={<EmptyState title="Sin datos para el período seleccionado" size="sm" />}
          caption="Ventas agrupadas por estado y tipo de documento"
        />
      )}
    </div>
  )
}

// ─── Tab: AR-Aging ────────────────────────────────────────────────────────────

// La escala sale del catálogo único: acá +90 salía en rosa claro, MÁS SUAVE que
// 61-90 (ver `components/ds/agingBuckets.ts`).
const BUCKET_LABELS: Record<string, string> = Object.fromEntries(AGING_BUCKETS.map(b => [b.key, b.label]))
const BUCKET_COLORS: Record<string, string> = Object.fromEntries(AGING_BUCKETS.map(b => [b.key, b.chip]))

function ARAgingTab() {
  const [params, setParams] = useState<ReportParams>(DEFAULT_PARAMS)
  const { data, isLoading, error } = useARAgingReport(params)
  const currency = data?.currency ?? 'CLP'
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)

  const handleExport = () => {
    if (!data?.data) return
    exportToExcel(
      data.data.map(r => ({
        'Cliente': r.customer_name,
        'Tipo': r.document_type,
        'Fecha Emisión': r.issue_date,
        'Fecha Vencimiento': r.due_date,
        'Días Vencido': r.days_overdue,
        'Total': r.total,
        'Por Cobrar': r.outstanding,
        'Bucket': BUCKET_LABELS[r.bucket] || r.bucket,
      })),
      'AR-Aging', 'reporte_ar_aging'
    )
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <DateFilters params={params} setParams={setParams} />
        <button onClick={handleExport} disabled={!data?.data?.length}
          className="flex items-center gap-2 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover transition-colors disabled:opacity-40">
          <Download size={15} /> Exportar Excel
        </button>
      </div>
      {/* Summary buckets */}
      {data?.summary && (
        <div className="grid grid-cols-5 gap-2 mb-4">
          {['current', '1_30', '31_60', '61_90', 'over_90'].map(b => {
            const s = (data.summary as unknown as Record<string, Record<string, string | number>>)[b]
            return (
              <div key={b} className={`rounded-lg p-3 text-center ${BUCKET_COLORS[b]}`}>
                <p className="text-xs font-medium">{BUCKET_LABELS[b]}</p>
                <p className="text-sm font-semibold mt-0.5">{fmtCur(s?.total ?? '0')}</p>
                <p className="text-xs opacity-70">{s?.count ?? 0} docs</p>
              </div>
            )
          })}
        </div>
      )}
      {isLoading ? <LoadingSkeleton /> : error ? (
        <p className="text-sm text-danger-solid">Error cargando reporte</p>
      ) : (
        <DataTable<ARAgingRow>
          columns={[
            { key: 'customer_name', header: 'Cliente', sortable: true,
              render: r => <span className="font-medium">{r.customer_name}</span> },
            { key: 'document_type', header: 'Tipo', sortable: true,
              render: r => <span className="font-mono text-xs">{r.document_type}</span> },
            { key: 'days_overdue', header: 'Días Vencido', numeric: true, sortable: true,
              sortValue: r => Number(r.days_overdue),
              render: r => (
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${BUCKET_COLORS[r.bucket]}`}>
                  {r.days_overdue}d
                </span>
              ) },
            { key: 'total', header: 'Total Doc.', numeric: true, sortable: true,
              sortValue: r => Number(r.total), render: r => fmtCur(r.total) },
            { key: 'outstanding', header: 'Por Cobrar', numeric: true, sortable: true,
              sortValue: r => Number(r.outstanding),
              render: r => <span className="font-semibold text-warning-content-soft">{fmtCur(r.outstanding)}</span> },
            { key: 'bucket', header: 'Estado', sortable: true,
              sortValue: r => BUCKET_LABELS[r.bucket] ?? r.bucket,
              render: r => (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${BUCKET_COLORS[r.bucket]}`}>
                  {BUCKET_LABELS[r.bucket]}
                </span>
              ) },
          ]}
          rows={data?.data ?? []}
          rowKey={r => r.document_id}
          surface={false}
          empty={<EmptyState title="Sin cuentas por cobrar pendientes" size="sm" />}
          caption="Antigüedad de saldos por cobrar, documento por documento"
        />
      )}
    </div>
  )
}

// ─── Tab: Compras ─────────────────────────────────────────────────────────────

/** Clave de un grupo del reporte. Sin `supplier_id` (backend anterior) no hay grupo que abrir. */
const purchasesGroupKey = (r: PurchasesReportRow) =>
  r.supplier_id ? `${r.supplier_id}|${r.status}` : ''

function PurchasesTab() {
  const [params, setParamsState] = useState<ReportParams>(DEFAULT_PARAMS)
  const tax = useTenantTax()
  const { data, isLoading, error } = usePurchasesReport(params)
  const currency = data?.currency ?? 'CLP'
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)
  // Un solo grupo abierto a la vez: el clic en la fila lo alterna (#1488).
  const [expandedKey, setExpandedKey] = useState('')
  // Cambiar el período rehace los grupos: el que estaba abierto puede ya no existir.
  const setParams = (p: ReportParams) => { setParamsState(p); setExpandedKey('') }
  const toggleGroup = (r: PurchasesReportRow) => {
    const k = purchasesGroupKey(r)
    if (k) setExpandedKey(prev => (prev === k ? '' : k))
  }

  // Abrir un documento sigue la misma regla que el listado de compras: con la
  // Vista 360 activa es el 360; sin el flag, el modal de detalle (el 360
  // devuelve al listado si el flag está apagado).
  const navigate = useNavigate()
  const { data: locConfig } = useLocalizationConfig()
  const use360 = !!locConfig?.ui_purchases_360
  const [detailId, setDetailId] = useState<string | null>(null)
  const { data: detailInvoice, isLoading: detailLoading } = useSupplierInvoice(detailId)
  const openInvoice = (id: string) => {
    if (use360) navigate(`/purchases/invoices/${id}/360`)
    else setDetailId(id)
  }

  const handleExport = () => {
    if (!data?.data) return
    exportToExcel(
      data.data.map(r => ({
        'Proveedor': r.supplier_name,
        'Estado': r.status_display ?? r.status,
        'Cantidad': r.count,
        'Subtotal': r.subtotal,
        [tax.name]: r.tax_amount,
        'Total': r.total,
      })),
      'Compras', 'reporte_compras'
    )
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <DateFilters params={params} setParams={setParams} />
        <button onClick={handleExport} disabled={!data?.data?.length}
          className="flex items-center gap-2 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover transition-colors disabled:opacity-40">
          <Download size={15} /> Exportar Excel
        </button>
      </div>
      {data?.summary && <SummaryCards summary={data.summary} currency={currency} />}
      {isLoading ? <LoadingSkeleton /> : error ? (
        <p className="text-sm text-danger-solid">Error cargando reporte</p>
      ) : (
        <DataTable<PurchasesReportRow>
          columns={[
            { key: 'supplier_name', header: 'Proveedor', sortable: true,
              // El botón es lo que hace operable el plegado con teclado: la fila
              // del DataTable responde al clic pero no recibe foco.
              // `stopPropagation` para no alternar dos veces (botón + fila).
              render: r => {
                const k = purchasesGroupKey(r)
                if (!k) return <span className="font-medium">{r.supplier_name}</span>
                const abierto = expandedKey === k
                return (
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); toggleGroup(r) }}
                    aria-expanded={abierto}
                    aria-label={`${abierto ? 'Ocultar' : 'Ver'} los documentos de ${r.supplier_name} (${r.status_display ?? r.status})`}
                    className="inline-flex items-center gap-1.5 text-left font-medium text-content rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                  >
                    {abierto
                      ? <ChevronDown size={14} className="shrink-0 text-content-muted" aria-hidden="true" />
                      : <ChevronRight size={14} className="shrink-0 text-content-muted" aria-hidden="true" />}
                    {r.supplier_name}
                  </button>
                )
              } },
            { key: 'status', header: 'Estado', sortable: true,
              sortValue: r => r.status_display ?? r.status,
              render: r => <span className="text-content-muted">{r.status_display ?? r.status}</span> },
            { key: 'count', header: 'Cant.', numeric: true, sortable: true,
              sortValue: r => Number(r.count) },
            { key: 'subtotal', header: 'Subtotal', numeric: true, sortable: true,
              sortValue: r => Number(r.subtotal), render: r => fmtCur(r.subtotal) },
            { key: 'tax_amount', header: tax.name, numeric: true, sortable: true,
              sortValue: r => Number(r.tax_amount), render: r => fmtCur(r.tax_amount) },
            { key: 'total', header: 'Total', numeric: true, sortable: true,
              sortValue: r => Number(r.total),
              render: r => <span className="font-semibold">{fmtCur(r.total)}</span> },
          ]}
          rows={data?.data ?? []}
          rowKey={(r, i) => purchasesGroupKey(r) || i}
          surface={false}
          onRowClick={toggleGroup}
          rowClickable={r => !!purchasesGroupKey(r)}
          rowExpanded={r => !!purchasesGroupKey(r) && expandedKey === purchasesGroupKey(r)}
          expandedRow={r => (
            <PurchasesGroupDetail
              supplierId={r.supplier_id!}
              status={r.status}
              dateFrom={params.date_from}
              dateTo={params.date_to}
              taxName={tax.name}
              fmtCur={fmtCur}
              use360={use360}
              onOpenInvoice={openInvoice}
            />
          )}
          empty={<EmptyState title="Sin datos para el período seleccionado" size="sm" />}
          caption="Compras agrupadas por proveedor y estado"
        />
      )}
      <SupplierInvoiceDetailModal
        open={!!detailId}
        onClose={() => setDetailId(null)}
        invoice={detailLoading ? null : (detailInvoice ?? null)}
      />
    </div>
  )
}

// ─── Tab: AP-Aging ────────────────────────────────────────────────────────────

function APAgingTab() {
  const [params, setParams] = useState<ReportParams>(DEFAULT_PARAMS)
  const { data, isLoading, error } = useAPAgingReport(params)
  const currency = data?.currency ?? 'CLP'
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)

  const handleExport = () => {
    if (!data?.data) return
    exportToExcel(
      data.data.map(r => ({
        'Proveedor': r.supplier_name,
        'Fecha Emisión': r.issue_date,
        'Fecha Vencimiento': r.due_date,
        'Días Vencido': r.days_overdue,
        'Estado': r.status,
        'Total': r.total,
        'Bucket': BUCKET_LABELS[r.bucket] || r.bucket,
      })),
      'AP-Aging', 'reporte_ap_aging'
    )
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <DateFilters params={params} setParams={setParams} />
        <button onClick={handleExport} disabled={!data?.data?.length}
          className="flex items-center gap-2 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover transition-colors disabled:opacity-40">
          <Download size={15} /> Exportar Excel
        </button>
      </div>
      {data?.summary && (
        <div className="grid grid-cols-5 gap-2 mb-4">
          {['current', '1_30', '31_60', '61_90', 'over_90'].map(b => {
            const s = (data.summary as unknown as Record<string, Record<string, string | number>>)[b]
            return (
              <div key={b} className={`rounded-lg p-3 text-center ${BUCKET_COLORS[b]}`}>
                <p className="text-xs font-medium">{BUCKET_LABELS[b]}</p>
                <p className="text-sm font-semibold mt-0.5">{fmtCur(s?.total ?? '0')}</p>
                <p className="text-xs opacity-70">{s?.count ?? 0} facturas</p>
              </div>
            )
          })}
        </div>
      )}
      {isLoading ? <LoadingSkeleton /> : error ? (
        <p className="text-sm text-danger-solid">Error cargando reporte</p>
      ) : (
        <DataTable<APAgingRow>
          columns={[
            { key: 'supplier_name', header: 'Proveedor', sortable: true,
              render: r => <span className="font-medium">{r.supplier_name}</span> },
            { key: 'days_overdue', header: 'Días Vencido', numeric: true, sortable: true,
              sortValue: r => Number(r.days_overdue),
              render: r => (
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${BUCKET_COLORS[r.bucket]}`}>
                  {r.days_overdue}d
                </span>
              ) },
            { key: 'total', header: 'Total', numeric: true, sortable: true,
              sortValue: r => Number(r.total),
              render: r => <span className="font-semibold">{fmtCur(r.total)}</span> },
            { key: 'due_date', header: 'Vencimiento', sortable: true,
              render: r => <span className="text-content-muted text-xs">{r.due_date || '—'}</span> },
            { key: 'bucket', header: 'Estado', sortable: true,
              sortValue: r => BUCKET_LABELS[r.bucket] ?? r.bucket,
              render: r => (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${BUCKET_COLORS[r.bucket]}`}>
                  {BUCKET_LABELS[r.bucket]}
                </span>
              ) },
          ]}
          rows={data?.data ?? []}
          rowKey={r => r.invoice_id}
          surface={false}
          empty={<EmptyState title="Sin cuentas por pagar pendientes" size="sm" />}
          caption="Antigüedad de saldos por pagar, factura por factura"
        />
      )}
    </div>
  )
}

// ─── Tab: Factoring ───────────────────────────────────────────────────────────

const OP_STATUS: Record<string, string> = {
  created: 'Creada', processing: 'Procesando', accepted: 'Aceptada',
  rejected: 'Rechazada', paid: 'Pagada',
}

function FactoringTab() {
  const [params, setParams] = useState<ReportParams>(DEFAULT_PARAMS)
  const { data, isLoading, error } = useFactoringReport(params)
  const currency = data?.currency ?? 'CLP'
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)

  const handleExport = () => {
    if (!data?.data) return
    exportToExcel(
      data.data.map(r => ({
        'Entidad': r.entity_name,
        'Fecha': r.operation_date,
        'Estado': OP_STATUS[r.status] || r.status,
        'Monto Cedido': r.total_amount_ceded,
        'Notas': r.notes,
      })),
      'Factoring', 'reporte_factoring'
    )
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <DateFilters params={params} setParams={setParams} />
        <button onClick={handleExport} disabled={!data?.data?.length}
          className="flex items-center gap-2 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover transition-colors disabled:opacity-40">
          <Download size={15} /> Exportar Excel
        </button>
      </div>
      {data?.summary && <SummaryCards summary={data.summary} currency={currency} />}
      {isLoading ? <LoadingSkeleton /> : error ? (
        <p className="text-sm text-danger-solid">Error cargando reporte</p>
      ) : (
        <DataTable<FactoringReportRow>
          columns={[
            { key: 'entity_name', header: 'Entidad', sortable: true,
              render: r => <span className="font-medium">{r.entity_name}</span> },
            { key: 'operation_date', header: 'Fecha', sortable: true,
              render: r => <span className="text-content-muted text-xs">{r.operation_date}</span> },
            { key: 'status', header: 'Estado', sortable: true,
              sortValue: r => OP_STATUS[r.status] || r.status,
              render: r => (
                <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-hover text-xs font-medium">
                  {OP_STATUS[r.status] || r.status}
                </span>
              ) },
            { key: 'total_amount_ceded', header: 'Monto Cedido', numeric: true, sortable: true,
              sortValue: r => Number(r.total_amount_ceded),
              render: r => <span className="font-semibold">{fmtCur(r.total_amount_ceded)}</span> },
            { key: 'notes', header: 'Notas',
              render: r => <span className="text-content-muted text-xs truncate max-w-32 block">{r.notes || '—'}</span> },
          ]}
          rows={data?.data ?? []}
          rowKey={r => r.operation_id}
          surface={false}
          empty={<EmptyState title="Sin operaciones de factoring" size="sm" />}
          caption="Operaciones de factoring del período"
        />
      )}
    </div>
  )
}

// ─── Tab: Ventas x Producto ───────────────────────────────────────────────────

function SalesByProductTab() {
  const [params, setParams] = useState<ReportParams>(DEFAULT_PARAMS)
  const { data, isLoading, error } = useSalesByProductReport(params)
  const currency = data?.currency ?? 'CLP'
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)

  const handleExport = () => {
    if (!data?.data) return
    exportToExcel(
      data.data.map(r => ({
        'Producto': r.name,
        'Cantidad': r.quantity,
        'Revenue': r.revenue,
        'N° Docs': r.doc_count,
      })),
      'Ventas x Producto', 'reporte_productos'
    )
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <DateFilters params={params} setParams={setParams} />
        <button onClick={handleExport} disabled={!data?.data?.length}
          className="flex items-center gap-2 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover transition-colors disabled:opacity-40">
          <Download size={15} /> Exportar Excel
        </button>
      </div>
      {data?.summary && <SummaryCards summary={data.summary} currency={currency} />}
      {isLoading ? <LoadingSkeleton /> : error ? (
        <p className="text-sm text-danger-solid">Error cargando reporte</p>
      ) : (
        <DataTable<SalesByProductRow>
          columns={[
            // El ranking sigue el orden que trae el backend, así que no se
            // ordena: numerar una tabla reordenada dejaría el «#» mintiendo.
            { key: 'rank', header: '#', width: 48,
              render: (_, i) => <span className="text-content-subtle">{i + 1}</span> },
            { key: 'name', header: 'Producto', sortable: true,
              render: r => <span className="font-medium">{r.name}</span> },
            { key: 'quantity', header: 'Cantidad', numeric: true, sortable: true,
              sortValue: r => Number(r.quantity),
              render: r => parseFloat(r.quantity).toLocaleString('es-CL') },
            { key: 'doc_count', header: 'N° Docs', numeric: true, sortable: true,
              sortValue: r => Number(r.doc_count) },
            { key: 'revenue', header: 'Revenue', numeric: true, sortable: true,
              sortValue: r => Number(r.revenue),
              render: r => <span className="font-semibold text-primary-hover">{fmtCur(r.revenue)}</span> },
          ]}
          rows={data?.data ?? []}
          rowKey={r => r.product_id}
          surface={false}
          empty={<EmptyState title="Sin ventas de productos en el período" size="sm" />}
          caption="Ventas agrupadas por producto"
        />
      )}
    </div>
  )
}

// ─── Tab: Clientes ────────────────────────────────────────────────────────────

function CustomerAnalysisTab() {
  const [params, setParams] = useState<ReportParams>(DEFAULT_PARAMS)
  const { data, isLoading, error } = useCustomerAnalysisReport(params)
  const currency = data?.currency ?? 'CLP'
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)

  const handleExport = () => {
    if (!data?.data) return
    exportToExcel(
      data.data.map(r => ({
        'Cliente': r.customer_name,
        'RUT/Tax ID': formatRut(r.tax_id),
        'Revenue Total': r.total_revenue,
        'N° Docs': r.doc_count,
        'Último Documento': r.last_document_date,
      })),
      'Clientes', 'reporte_clientes'
    )
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <DateFilters params={params} setParams={setParams} />
        <button onClick={handleExport} disabled={!data?.data?.length}
          className="flex items-center gap-2 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover transition-colors disabled:opacity-40">
          <Download size={15} /> Exportar Excel
        </button>
      </div>
      {data?.summary && <SummaryCards summary={data.summary} currency={currency} />}
      {isLoading ? <LoadingSkeleton /> : error ? (
        <p className="text-sm text-danger-solid">Error cargando reporte</p>
      ) : (
        <DataTable<CustomerAnalysisRow>
          columns={[
            { key: 'rank', header: '#', width: 48,
              render: (_, i) => <span className="text-content-subtle">{i + 1}</span> },
            { key: 'customer_name', header: 'Cliente', sortable: true,
              render: r => <span className="font-medium">{r.customer_name}</span> },
            { key: 'tax_id', header: 'Tax ID', sortable: true,
              render: r => <span className="text-content-muted font-mono text-xs">{formatRut(r.tax_id)}</span> },
            { key: 'doc_count', header: 'N° Docs', numeric: true, sortable: true,
              sortValue: r => Number(r.doc_count) },
            { key: 'last_document_date', header: 'Último Doc.', numeric: true, sortable: true,
              render: r => <span className="text-xs text-content-muted">{r.last_document_date || '—'}</span> },
            { key: 'total_revenue', header: 'Revenue', numeric: true, sortable: true,
              sortValue: r => Number(r.total_revenue),
              render: r => <span className="font-semibold text-success-content">{fmtCur(r.total_revenue)}</span> },
          ]}
          rows={data?.data ?? []}
          rowKey={r => r.customer_id}
          surface={false}
          empty={<EmptyState title="Sin datos de clientes en el período" size="sm" />}
          caption="Clientes ordenados por facturación del período"
        />
      )}
    </div>
  )
}

// ─── Margen de contribución escalonado ───────────────────────────────────────
// Los costos de estructura NO se prorratean entre los centros que venden: se
// restan al final. Evita el reparto por drivers, que siempre termina en discusión
// sobre a qué área le tocó más overhead.

function ContributionCard({ c, currency }: { c: ContributionSummary; currency: string }) {
  const resultado = parseFloat(c.operating_result)
  const hayPasante = parseFloat(c.passthrough_revenue) !== 0 || parseFloat(c.passthrough_expense) !== 0

  const linea = (label: string, valor: string, opts?: { sangria?: boolean; tenue?: boolean }) => (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <span className={`text-sm ${opts?.sangria ? 'pl-4' : ''} ${opts?.tenue ? 'text-content-subtle' : 'text-content-muted'}`}>
        {label}
      </span>
      <span className={`text-sm tabular-nums ${opts?.tenue ? 'text-content-subtle' : 'text-content'}`}>
        {fmt(valor, true, currency)}
      </span>
    </div>
  )

  return (
    <div className="bg-glass backdrop-blur-glass border border-divider rounded-card p-4 shadow-card">
      <h3 className="text-sm font-semibold text-content mb-3">Margen de contribución</h3>

      {linea('Aporte de los centros que venden', c.contribution_margin)}
      {linea('− Estructura imputada a centros de costo', c.structure_assigned, { sangria: true, tenue: true })}
      {linea('− Estructura sin centro asignado', c.structure_unassigned, { sangria: true, tenue: true })}

      <div className="flex items-baseline justify-between gap-4 pt-2 mt-1 border-t border-divider">
        <span className="text-sm font-semibold text-content">Resultado operacional</span>
        <span className={`text-base font-semibold tabular-nums ${
          resultado >= 0 ? 'text-success-content' : 'text-danger-content-soft'
        }`}>
          {fmt(c.operating_result, true, currency)}
        </span>
      </div>

      {c.mistyped_centers?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-divider">
          <p className="text-xs font-medium text-warning-content mb-1">
            {c.mistyped_centers.length === 1
              ? '1 centro de costo recibió ingresos'
              : `${c.mistyped_centers.length} centros de costo recibieron ingresos`}
          </p>
          <p className="text-xs text-content-subtle mb-1.5">
            Si venden, son centros de beneficio. Mientras sigan como «centro de costo» su
            ingreso no entra al margen y el resultado de arriba queda subestimado.
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-muted">
            {c.mistyped_centers.map(m => (
              <span key={m.code}>
                <span className="font-mono">{m.code}</span> {fmt(m.revenue, true, currency)}
              </span>
            ))}
          </div>
        </div>
      )}

      {hayPasante && (
        <div className="mt-3 pt-3 border-t border-divider">
          <p className="text-xs text-content-subtle mb-1.5">
            Pasante — dinero que solo cruza la empresa. No suma al margen.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-content-muted">
            <span>Ingreso {fmt(c.passthrough_revenue, true, currency)}</span>
            <span>Costo {fmt(c.passthrough_expense, true, currency)}</span>
            <span className="text-content">Margen {fmt(c.passthrough_margin, true, currency)}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Tab: Centros de Costo ───────────────────────────────────────────────────

function CostCenterTab() {
  const [params, setParams] = useState<ReportParams>(DEFAULT_PARAMS)
  const { data, isLoading, error } = useCostCenterDistributionReport(params)
  const currency = data?.currency ?? 'CLP'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <DateFilters params={params} setParams={setParams} />
        {data?.data && data.data.length > 0 && (
          <button
            onClick={() => exportToExcel(
              data.data.map(r => ({
                'Código': r.code,
                'Centro de Costo': r.name,
                'Ingresos': r.revenue,
                'Gastos': r.expense,
                'Margen': r.margin,
              })),
              'Centros de Costo',
              'centros_de_costo',
            )}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-success-fill text-white text-xs font-medium rounded-lg hover:bg-success-fill-hover"
          >
            <Download size={13} /> Exportar Excel
          </button>
        )}
      </div>

      {data?.summary && <SummaryCards summary={data.summary} currency={currency} />}

      {isLoading && <LoadingSkeleton />}
      {error && <p className="text-sm text-danger-solid">Error cargando reporte</p>}

      {data?.contribution && <ContributionCard c={data.contribution} currency={currency} />}

      {data?.data && data.data.length > 0 && (
        <DataTable<CostCenterDistributionRow>
          columns={[
            { key: 'code', header: 'Código', sortable: true,
              render: r => <span className="font-mono text-xs">{r.code}</span> },
            { key: 'name', header: 'Centro de Costo', sortable: true },
            { key: 'center_type_label', header: 'Tipo', sortable: true,
              render: r => (
                <span className="text-xs px-1.5 py-0.5 rounded bg-surface-muted text-content-muted">
                  {r.center_type_label}
                </span>
              ) },
            { key: 'revenue', header: 'Ingresos', numeric: true, sortable: true,
              sortValue: r => Number(r.revenue),
              render: r => <span className="text-success-content">{fmt(r.revenue, true, currency)}</span> },
            { key: 'expense', header: 'Gastos', numeric: true, sortable: true,
              sortValue: r => Number(r.expense),
              render: r => <span className="text-danger-content-soft">{fmt(r.expense, true, currency)}</span> },
            // A un centro de costo no se le mide margen: no vende. Mostrarlo en
            // rojo sugeriría que rinde mal, cuando solo es estructura.
            { key: 'margin', header: 'Margen', numeric: true, sortable: true,
              sortValue: r => (r.center_type === 'cost' ? null : Number(r.margin)),
              render: r => {
                if (r.center_type === 'cost') {
                  return r.has_unexpected_revenue ? (
                    <span
                      className="text-warning-content text-xs"
                      title="Este centro recibió ingresos pero está tipificado como centro de costo. Si vende, debería ser centro de beneficio."
                    >
                      ⚠ revisar tipo
                    </span>
                  ) : (
                    <span className="text-content-subtle text-xs">estructura</span>
                  )
                }
                const margin = parseFloat(r.margin)
                return (
                  <span className={`font-medium ${margin >= 0 ? 'text-success-content' : 'text-danger-content-soft'}`}>
                    {fmt(r.margin, true, currency)}
                  </span>
                )
              } },
          ]}
          rows={data.data}
          rowKey={r => r.cost_center_id}
          surface={false}
          caption="Ingresos, gastos y margen por centro de costo"
        />
      )}

      {!isLoading && !error && !data?.data?.length && (
        <EmptyState title="Sin datos de centros de costo en el período" size="sm" />
      )}
    </div>
  )
}

// ─── Tab: F29 ────────────────────────────────────────────────────────────────

const MONTH_OPTS = [
  { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' },
]

function F29DetailsPanel({ month, year }: { month: number; year: number }) {
  // ⚠️ Sin orden por clic a propósito: esta lista la pagina el servidor (50 por
  // página) y mezcla ventas, compras y honorarios. Ordenar en el navegador
  // ordenaría solo la página visible sin avisar.
  const locale = useTenantLocale()
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<F29DetailsType>('sales')
  const [page, setPage] = useState(1)
  const pageSize = 50
  const { data, isLoading } = useF29Details({
    type: tab, month, year, page, page_size: pageSize, enabled: open,
  })

  const handleToggleTab = (t: F29DetailsType) => { setTab(t); setPage(1) }
  const totalPages = data ? Math.max(1, Math.ceil(data.count / pageSize)) : 1

  const TAB_LABEL: Record<F29DetailsType, string> = {
    sales: 'Ventas', purchases: 'Compras', honorarios: 'Honorarios',
  }

  return (
    <div className="mt-2 border border-divider rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-surface-muted hover:bg-soft transition-colors text-sm font-medium text-content"
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          Ver registros incluidos en el cálculo
        </span>
        <span className="text-xs text-content-subtle">
          {open ? 'Ocultar' : 'Auditoría / trazabilidad'}
        </span>
      </button>

      {open && (
        <div className="p-4">
          <div className="flex gap-2 mb-3 border-b border-divider">
            {(['sales', 'purchases', 'honorarios'] as const).map(t => (
              <button
                key={t}
                onClick={() => handleToggleTab(t)}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                  tab === t ? 'border-primary text-primary' : 'border-transparent text-content-muted hover:text-content'
                }`}
              >
                {TAB_LABEL[t]}
                {data && tab === t ? ` (${data.count})` : ''}
              </button>
            ))}
          </div>

          {isLoading ? (
            <p className="text-xs text-content-subtle text-center py-6">Cargando…</p>
          ) : !data || data.results.length === 0 ? (
            <EmptyState title="Sin registros en el periodo." size="sm" />
          ) : (
            <>
              {tab === 'honorarios' ? (
                <DataTable<F29HonorariosRow>
                  columns={[
                    { key: 'issue_date', header: 'Fecha',
                      render: r => r.issue_date ?? '—' },
                    { key: 'bhe_number', header: 'N° BHE',
                      render: r => <span className="font-mono">{r.bhe_number || '—'}</span> },
                    { key: 'issuer_name', header: 'Emisor',
                      render: r => r.issuer_name || '—' },
                    { key: 'issuer_rut', header: 'RUT',
                      render: r => <span className="font-mono text-content-subtle">{r.issuer_rut}</span> },
                    { key: 'total_honorarios', header: 'Bruto', numeric: true,
                      sortValue: r => Number(r.total_honorarios), render: r => fmt(r.total_honorarios) },
                    { key: 'impuesto_honorarios', header: 'Retención', numeric: true,
                      sortValue: r => Number(r.impuesto_honorarios), render: r => fmt(r.impuesto_honorarios) },
                    { key: 'liquido_honorarios', header: 'Líquido', numeric: true,
                      sortValue: r => Number(r.liquido_honorarios),
                      render: r => <span className="font-semibold">{fmt(r.liquido_honorarios)}</span> },
                  ]}
                  rows={data.results as F29HonorariosRow[]}
                  rowKey={r => r.id}
                  surface={false}
                  caption="Boletas de honorarios del período declarado"
                />
              ) : (
                <DataTable<F29SaleRow | F29PurchaseRow>
                  columns={[
                    { key: 'document_type', header: 'Tipo',
                      sortValue: r => (tab === 'sales'
                        ? (r as F29SaleRow).document_type
                        : (r as F29PurchaseRow).document_type_sii || ''),
                      render: r => (
                        <span className="font-mono">
                          {tab === 'sales'
                            ? (r as F29SaleRow).document_type
                            : ((r as F29PurchaseRow).document_type_sii || '—')}
                        </span>
                      ) },
                    { key: 'number', header: 'Número',
                      sortValue: r => (tab === 'sales'
                        ? (r as F29SaleRow).number
                        : (r as F29PurchaseRow).invoice_number),
                      render: r => (
                        <span className="font-mono">
                          {tab === 'sales' ? (r as F29SaleRow).number : (r as F29PurchaseRow).invoice_number}
                        </span>
                      ) },
                    { key: 'issue_date', header: 'Fecha',
                      render: r => r.issue_date ?? '—' },
                    { key: 'party', header: tab === 'sales' ? 'Cliente' : 'Proveedor',
                      sortValue: r => (tab === 'sales'
                        ? (r as F29SaleRow).customer_name
                        : (r as F29PurchaseRow).supplier_name),
                      render: r => (tab === 'sales'
                        ? (r as F29SaleRow).customer_name
                        : (r as F29PurchaseRow).supplier_name) },
                    { key: 'subtotal', header: 'Neto', numeric: true,
                      sortValue: r => Number(r.subtotal), render: r => fmt(r.subtotal) },
                    { key: 'tax_amount', header: 'IVA', numeric: true,
                      sortValue: r => Number(r.tax_amount), render: r => fmt(r.tax_amount) },
                    { key: 'total', header: 'Total', numeric: true,
                      sortValue: r => Number(r.total), render: r => fmt(r.total) },
                  ]}
                  rows={data.results as (F29SaleRow | F29PurchaseRow)[]}
                  rowKey={r => r.id}
                  surface={false}
                  caption={tab === 'sales'
                    ? 'Documentos de venta que componen el período declarado'
                    : 'Documentos de compra que componen el período declarado'}
                />
              )}

              {totalPages > 1 && (
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  total={data.count}
                  onPageChange={setPage}
                  locale={locale}
                  className="mt-3"
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}


/** Celda del F29: código SII + valor + botón copiar (valor entero para pegar al portal). */
function F29Code({ code, value, kind = 'money' }: { code?: string | null; value: string; kind?: 'money' | 'count' }) {
  const [copied, setCopied] = useState(false)
  const num = parseFloat(value || '0')
  // Las casillas del formulario del SII no llevan signo: las notas de crédito
  // (510, 528) se declaran en positivo y el formulario las resta (#1594). En
  // pantalla se mantiene el signo; al portapapeles va el valor absoluto.
  const intVal = String(Math.abs(Math.round(num)))
  const display = kind === 'money' ? fmt(value) : String(Math.round(num))
  const copy = () => {
    navigator.clipboard?.writeText(intVal).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1200)
    })
  }
  return (
    <div className="flex items-center justify-end gap-2">
      {code && (
        <span className="font-mono text-[11px] text-content-subtle bg-surface-muted rounded px-1.5 py-0.5 border border-divider shrink-0">
          {code}
        </span>
      )}
      <span className="font-mono text-sm text-content tabular-nums">{display}</span>
      <button type="button" onClick={copy} title="Copiar valor para el SII"
        className="p-1 rounded hover:bg-soft text-content-subtle shrink-0">
        {copied ? <Check size={13} className="text-success-content-soft" /> : <Copy size={13} />}
      </button>
    </div>
  )
}

function F29Tab() {
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const { data, isLoading, error } = useF29({ month, year })

  const handleExport = () => {
    if (!data) return
    const rows = (data.ventas.by_doc_type ?? []).map(d => ({
      'Tipo': d.code,
      'Documento': d.name,
      'N Docs': d.count,
      'Neto': d.neto,
      'IVA': d.iva,
    }))
    rows.push({
      'Tipo': '',
      'Documento': 'TOTAL COMPRAS',
      'N Docs': data.compras.count,
      'Neto': data.compras.neto,
      'IVA': data.compras.iva_credito,
    })
    exportToExcel(rows, 'F29', `f29_${year}_${String(month).padStart(2, '0')}`)
  }

  const ivaNeto = data ? parseFloat(data.iva_neto) : 0
  const isAPagar = data?.resultado === 'a_pagar'
  const [pdfLoading, setPdfLoading] = useState(false)

  const handleDownloadPdf = async () => {
    setPdfLoading(true)
    try {
      const r = await apiClient.get('/reports/f29/pdf/', { params: { month, year }, responseType: 'blob' })
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `f29_${year}_${String(month).padStart(2, '0')}.pdf`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 10000)
    } finally {
      setPdfLoading(false)
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <select aria-label="Mes" value={month} onChange={e => setMonth(parseInt(e.target.value))}
            className="border border-divider rounded-lg px-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary">
            {MONTH_OPTS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select aria-label="Año" value={year} onChange={e => setYear(parseInt(e.target.value))}
            className="border border-divider rounded-lg px-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary">
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleDownloadPdf} disabled={!data || pdfLoading}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-on-primary rounded-lg text-sm hover:bg-primary-600 transition-colors disabled:opacity-40">
            <FileDown size={15} /> {pdfLoading ? 'Generando…' : 'Descargar PDF'}
          </button>
          <button onClick={handleExport} disabled={!data}
            className="flex items-center gap-2 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover transition-colors disabled:opacity-40">
            <Download size={15} /> Exportar Excel
          </button>
        </div>
      </div>

      {isLoading ? <LoadingSkeleton /> : error ? (
        <p className="text-sm text-danger-solid">Error cargando F29</p>
      ) : data ? (
        <>
          {/* Aviso: guía de referencia */}
          <div className="mb-4 rounded-lg border border-warning-border bg-warning-surface px-3 py-2 text-xs text-warning-content-strong">
            Vista de referencia para llenar el F29 en el portal del SII. Cada valor muestra su código y se copia
            como número entero. No es la declaración oficial.
          </div>

          <div className="border border-divider rounded-xl overflow-hidden divide-y divide-divider">
            {/* ── VENTAS (Débitos) ── */}
            <div className="bg-surface-muted px-4 py-2.5 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-content">Ventas</h3>
              <div className="hidden sm:flex gap-12 text-xs text-content-muted uppercase tracking-wider pr-9">
                <span className="w-28 text-right">Cantidad</span>
                <span className="w-28 text-right">Neto / IVA débito</span>
              </div>
            </div>
            {(data.ventas.by_doc_type ?? []).filter(r => r.code !== 'FC').map((row, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-4">
                <span className="text-sm text-content">{row.name}</span>
                <div className="flex gap-6 sm:gap-12">
                  <div className="w-32 sm:w-36"><F29Code code={row.cant_code} value={String(row.count)} kind="count" /></div>
                  {/* Exentas y exportaciones se declaran por su NETO (no tienen IVA); el resto, por el IVA débito */}
                  <div className="w-36 sm:w-40">
                    <F29Code code={row.iva_code ?? row.neto_code} value={row.iva_code ? row.iva : row.neto} />
                  </div>
                </div>
              </div>
            ))}
            <div className="px-4 py-2.5 flex items-center justify-between gap-4 bg-row-alt/40">
              <span className="text-sm font-semibold text-content">Total IVA débitos</span>
              <div className="w-36 sm:w-40 pr-0"><F29Code code={data.codigos?.total_debitos ?? '538'} value={data.ventas.iva_debito} /></div>
            </div>

            {/* ── COMPRAS (Créditos) ── */}
            <div className="bg-surface-muted px-4 py-2.5 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-content">Compras</h3>
              <div className="hidden sm:flex gap-12 text-xs text-content-muted uppercase tracking-wider pr-9">
                <span className="w-28 text-right">Cantidad</span>
                <span className="w-28 text-right">Neto / IVA</span>
              </div>
            </div>
            {(data.compras.by_purchase_type && data.compras.by_purchase_type.length > 0) ? (
              data.compras.by_purchase_type.map((row) => (
                <div key={row.key} className="px-4 py-2.5 flex items-center justify-between gap-4">
                  <span className="text-sm text-content">{row.name}</span>
                  <div className="flex gap-6 sm:gap-12">
                    <div className="w-32 sm:w-36"><F29Code code={row.cant_code} value={String(row.count)} kind="count" /></div>
                    {/* Exentas muestran neto (sin IVA); el resto, IVA crédito */}
                    <div className="w-36 sm:w-40">
                      <F29Code code={row.iva_code ?? row.neto_code} value={row.iva_code ? row.iva : row.neto} />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-2.5 flex items-center justify-between gap-4">
                <span className="text-sm text-content">IVA documentos electrónicos recibidos ({data.compras.count})</span>
                <div className="w-36 sm:w-40"><F29Code code={data.codigos?.iva_credito ?? '511'} value={data.compras.iva_credito} /></div>
              </div>
            )}
            <div className="px-4 py-2.5 flex items-center justify-between gap-4 bg-row-alt/40">
              <span className="text-sm font-semibold text-content">Total créditos</span>
              <div className="w-36 sm:w-40"><F29Code code={data.codigos?.total_creditos ?? '537'} value={data.compras.iva_credito} /></div>
            </div>

            {/* ── Retenciones / honorarios ── */}
            {(parseFloat(data.honorarios?.retencion_2da_cat ?? '0') > 0 ||
              parseFloat(data.impuesto_unico ?? '0') > 0) && (
              <>
                <div className="bg-surface-muted px-4 py-2.5">
                  <h3 className="text-sm font-semibold text-content">Retenciones e impuestos</h3>
                </div>
                {parseFloat(data.honorarios?.retencion_2da_cat ?? '0') > 0 && (
                  <div className="px-4 py-2.5 flex items-center justify-between gap-4">
                    <span className="text-sm text-content">Retención 2da categoría (honorarios · {data.honorarios?.count ?? 0} BHE)</span>
                    <div className="w-36 sm:w-40"><F29Code code={data.codigos?.retencion_2da_cat ?? '151'} value={data.honorarios?.retencion_2da_cat ?? '0'} /></div>
                  </div>
                )}
                {parseFloat(data.impuesto_unico ?? '0') > 0 && (
                  <div className="px-4 py-2.5 flex items-center justify-between gap-4">
                    <span className="text-sm text-content">Impuesto Único a los Trabajadores (nómina del período)</span>
                    <div className="w-36 sm:w-40"><F29Code code={data.codigos?.impuesto_unico ?? '48'} value={data.impuesto_unico ?? '0'} /></div>
                  </div>
                )}
              </>
            )}

            {/* ── PPM ── */}
            {data.ppm && (
              <>
                <div className="bg-surface-muted px-4 py-2.5">
                  <h3 className="text-sm font-semibold text-content">PPM — Pago Provisional Mensual</h3>
                </div>
                <div className="px-4 py-2.5 flex items-center justify-between gap-4">
                  <span className="text-sm text-content">
                    Base imponible PPM
                    <span className="text-xs text-content-subtle ml-2">ingresos brutos del giro</span>
                  </span>
                  <div className="w-36 sm:w-40"><F29Code code={data.codigos?.base_ppm ?? '563'} value={data.ppm.base} /></div>
                </div>
                <div className="px-4 py-2.5 flex items-center justify-between gap-4">
                  <span className="text-sm text-content">
                    PPM obligatorio
                    <span className="text-xs text-content-subtle ml-2">
                      {parseFloat(data.ppm.tasa) > 0
                        ? `tasa ${data.ppm.tasa}%`
                        : 'sin tasa configurada (Config → Localización)'}
                    </span>
                  </span>
                  <div className="w-36 sm:w-40"><F29Code code={data.codigos?.ppm ?? '062'} value={data.ppm.monto} /></div>
                </div>
              </>
            )}

            {/* ── Resultado ── */}
            <div className="bg-surface-muted px-4 py-2.5">
              <h3 className="text-sm font-semibold text-content">Resultado</h3>
            </div>
            <div className="px-4 py-2.5 flex items-center justify-between gap-4">
              <span className="text-sm text-content">IVA determinado {isAPagar ? '(a pagar)' : '(remanente a favor)'}</span>
              <div className="w-36 sm:w-40"><F29Code code={data.codigos?.iva_determinado ?? '89'} value={String(Math.abs(ivaNeto))} /></div>
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-4 bg-warning-surface">
              <span className="text-sm font-bold text-warning-content">Total F29</span>
              <div className="w-36 sm:w-40"><F29Code code={data.codigos?.total_a_pagar ?? '595'} value={data.total_a_pagar ?? '0'} /></div>
            </div>
          </div>

          <div className="mt-4">
            <F29DetailsPanel month={month} year={year} />
          </div>
        </>
      ) : (
        <p className="text-sm text-content-subtle text-center py-6">Seleccione un periodo para ver el F29</p>
      )}
    </div>
  )
}

// ─── F22: Estimación Impuesto a la Renta (Chile) ────────────────────────────

/**
 * Las dos tablas de desglose del F22 —ingresos y gastos por cuenta— tienen la
 * misma forma. Declararlas una vez evita que se separen: escritas aparte, la de
 * gastos terminó con otra alineación de monto más de una vez.
 */
function columnasBreakdown(fmtCLP: (v: string | number) => string): DataTableColumn<F22BreakdownRow>[] {
  return [
    { key: 'code', header: 'Código', sortable: true,
      render: r => <span className="font-mono">{r.code}</span> },
    { key: 'name', header: 'Cuenta', sortable: true },
    { key: 'saldo', header: 'Saldo', numeric: true, sortable: true,
      sortValue: r => Number(r.saldo), render: r => fmtCLP(r.saldo) },
  ]
}

function F22Tab() {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear() - 1)  // año anterior por default (cierre)
  const [tasa, setTasa] = useState('0.27')
  const { data, isLoading, error } = useF22({ year, tasa })

  // El Formulario 22 es una declaración del SII y se presenta en pesos chilenos:
  // acá el CLP y el `es-CL` NO son un default olvidado, son el formato del
  // documento. No migrar a la moneda del tenant.
  const fmtCLP = (v: string | number | undefined) =>
    new Intl.NumberFormat(tenantLocale(), { style: 'currency', currency: tenantCurrency() })
      .format(Number(v ?? 0))

  const handleExport = () => {
    if (!data) return
    const rows: any[] = []
    rows.push({ 'Concepto': 'Año fiscal', 'Valor': data.year })
    rows.push({ 'Concepto': 'Régimen', 'Valor': data.tasa_label })
    rows.push({ 'Concepto': 'Ingresos del ejercicio', 'Valor': data.ingresos })
    rows.push({ 'Concepto': 'Gastos del ejercicio', 'Valor': data.gastos })
    rows.push({ 'Concepto': 'Utilidad / (Pérdida) financiera', 'Valor': data.utilidad })
    rows.push({ 'Concepto': `Impuesto estimado (tasa ${data.tasa})`, 'Valor': data.impuesto_estimado })
    rows.push({ 'Concepto': 'PPM pagado durante el año', 'Valor': data.ppm_pagado })
    rows.push({ 'Concepto': 'Impuesto neto a pagar / (devolución)', 'Valor': data.impuesto_neto })
    if (data.reajuste_art72_monto && parseFloat(data.reajuste_art72_monto) > 0) {
      rows.push({ 'Concepto': `Reajuste Art. 72 LIR (${data.reajuste_art72_pct}%, cód. 114)`, 'Valor': data.reajuste_art72_monto })
      rows.push({ 'Concepto': 'Impuesto neto reajustado a pagar', 'Valor': data.impuesto_neto_reajustado })
    }
    exportToExcel(rows, 'F22', `f22_${year}`)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="reportscenterpage-ano-fiscal" className="block text-xs font-medium text-content-muted mb-1">Año fiscal</label>
          <input id="reportscenterpage-ano-fiscal"
            type="number"
            value={year}
            onChange={e => setYear(parseInt(e.target.value, 10) || year)}
            min={2018} max={2100}
            className="w-28 px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content"
          />
        </div>
        <div>
          <label htmlFor="reportscenterpage-regimen-tributario" className="block text-xs font-medium text-content-muted mb-1">Régimen tributario</label>
          <select id="reportscenterpage-regimen-tributario"
            value={tasa}
            onChange={e => setTasa(e.target.value)}
            className="px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content"
          >
            <option value="0.27">General (semi-integrado) — 27%</option>
            <option value="0.25">ProPyme General — 25%</option>
            <option value="0.10">ProPyme Transparente — 10%</option>
          </select>
        </div>
        <button
          onClick={handleExport}
          disabled={!data}
          className="ml-auto px-3 py-2 text-sm border border-divider rounded-lg hover:bg-soft disabled:opacity-50"
        >
          Exportar Excel
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-content-subtle text-center py-6">Calculando…</p>
      ) : error ? (
        <p className="text-sm text-danger-solid">Error cargando F22</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-4 bg-success-surface rounded-lg">
              <p className="text-xs text-content-muted">Ingresos del ejercicio</p>
              <p className="text-lg font-bold text-success-content mt-1">{fmtCLP(data.ingresos)}</p>
            </div>
            <div className="p-4 bg-danger-surface rounded-lg">
              <p className="text-xs text-content-muted">Gastos del ejercicio</p>
              <p className="text-lg font-bold text-danger-content mt-1">{fmtCLP(data.gastos)}</p>
            </div>
            <div className={`p-4 rounded-lg ${data.es_perdida ? 'bg-danger-surface' : 'bg-primary-50'}`}>
              <p className="text-xs text-content-muted">{data.es_perdida ? 'Pérdida financiera' : 'Utilidad financiera'}</p>
              <p className={`text-lg font-bold mt-1 ${data.es_perdida ? 'text-danger-content' : 'text-primary'}`}>
                {fmtCLP(data.utilidad)}
              </p>
            </div>
          </div>

          {/*
            ⚠️ Esta NO se migra a `DataTable`, y es deliberado: no es una tabla de
            datos sino el desarrollo de un cálculo —régimen, base, impuesto, PPM,
            reajuste y resultado—, donde cada fila dice algo distinto y varias son
            condicionales. No tiene encabezados porque no hay columnas que nombrar,
            y `DataTable` se los agregaría. Es el caso «lista sin thead» del
            inventario del barrido.
          */}
          <div className="border border-divider rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-divider">
                <tr className="bg-surface-muted">
                  <td className="py-2.5 px-3 font-medium text-content">Régimen</td>
                  <td className="py-2.5 px-3 text-right text-content">
                    {data.tasa_label}
                    <span className="text-xs text-content-subtle ml-2">
                      {data.tasa_origen === 'regimen' ? '(según configuración)'
                        : data.tasa_origen === 'manual' ? '(tasa manual)' : '(por defecto)'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="py-2.5 px-3">
                    Base imponible (RLI)
                    <span className="text-xs text-content-subtle ml-2 font-mono">cód. {data.codigos?.base_imponible ?? '643'}</span>
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono">{fmtCLP(data.utilidad)}</td>
                </tr>
                <tr>
                  <td className="py-2.5 px-3">Impuesto Primera Categoría estimado ({(parseFloat(data.tasa)*100).toFixed(0)}%)</td>
                  <td className="py-2.5 px-3 text-right font-mono">{fmtCLP(data.impuesto_estimado)}</td>
                </tr>
                <tr>
                  <td className="py-2.5 px-3">
                    PPM pagado durante {data.year}
                    <span className="text-xs text-content-subtle ml-2 font-mono">cód. {data.codigos?.ppm ?? '36'}</span>
                    {data.ppm_account_code && (
                      <span className="text-xs text-content-subtle ml-2">(cuenta {data.ppm_account_code})</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-success-content-soft">−{fmtCLP(data.ppm_pagado)}</td>
                </tr>
                {data.reajuste_art72_monto && parseFloat(data.reajuste_art72_monto) > 0 && (
                  <tr>
                    <td className="py-2.5 px-3">
                      Reajuste Art. 72 LIR ({data.reajuste_art72_pct}%)
                      <span className="text-xs text-content-subtle ml-2 font-mono">cód. {data.codigos?.reajuste_art72 ?? '114'}</span>
                      {data.anio_tributario && (
                        <span className="text-xs text-content-subtle ml-2">(AT {data.anio_tributario})</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-warning-content-soft">+{fmtCLP(data.reajuste_art72_monto)}</td>
                  </tr>
                )}
                <tr className="bg-primary-50">
                  <td className="py-3 px-3 font-bold text-content">
                    {data.resultado === 'a_pagar' && 'Impuesto neto a PAGAR'}
                    {data.resultado === 'a_devolver' && 'Saldo a DEVOLVER'}
                    {data.resultado === 'cero' && 'Sin saldo'}
                  </td>
                  <td className={`py-3 px-3 text-right font-bold font-mono text-lg ${
                    data.resultado === 'a_pagar' ? 'text-warning-content-soft' :
                    data.resultado === 'a_devolver' ? 'text-success-content' : 'text-content-muted'
                  }`}>
                    {fmtCLP(Math.abs(parseFloat(data.impuesto_neto_reajustado ?? data.impuesto_neto)))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-warning-surface border border-warning-border rounded-lg p-3 text-xs text-warning-content-strong">
            <p className="font-semibold">⚠️ Estimación referencial</p>
            <p className="mt-1">{data.disclaimer}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-semibold text-content mb-2">Top ingresos por cuenta</h4>
              <DataTable<F22BreakdownRow>
                columns={columnasBreakdown(fmtCLP)}
                rows={data.ingresos_breakdown.slice(0, 15)}
                rowKey={r => r.code}
                caption="Cuentas de ingreso con mayor saldo del ejercicio"
              />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-content mb-2">Top gastos por cuenta</h4>
              <DataTable<F22BreakdownRow>
                columns={columnasBreakdown(fmtCLP)}
                rows={data.gastos_breakdown.slice(0, 15)}
                rowKey={r => r.code}
                caption="Cuentas de gasto con mayor saldo del ejercicio"
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}

// ─── 10. Reporte de Comisiones ────────────────────────────────────────────────

function CommissionReportTab() {
  const [params, setParams] = useState<ReportParams>(DEFAULT_PARAMS)
  const { data, isLoading, error } = useCommissionReport(params)
  const currency = data?.currency ?? 'CLP'
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)

  const handleExport = () => {
    if (!data?.data?.length) return
    exportToExcel(
      data.data.map(r => ({
        'Pedido':           r.order_number,
        'Fecha':            r.order_date,
        'Vendedor':         r.seller_name,
        'SKU':              r.sku,
        'Base comisión':    r.commission_base_label,
        'Tasa margen (%)':  r.margin_rate,
        'Neto línea':       r.line_net,
        'Margen empresa':   r.margin_amount,
        'Tasa comisión (%)': r.commission_rate,
        'Origen de la tasa': r.commission_source_label,
        'Comisión':         r.commission_amount,
      })),
      'Comisiones', 'reporte_comisiones'
    )
  }

  const summary = data?.summary
  const hasMarginLines = data?.data?.some(r => r.commission_base === 'margin')

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <DateFilters params={params} setParams={setParams} />
        <button onClick={handleExport} disabled={!data?.data?.length}
          className="flex items-center gap-2 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover transition-colors disabled:opacity-40">
          <Download size={15} /> Exportar Excel
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-glass-3 rounded-lg p-3 border border-divider">
            <p className="text-xs text-content-muted">Total comisiones</p>
            <p className="text-base font-semibold text-content mt-0.5">{fmtCur(summary.total_commissions)}</p>
          </div>
          <div className="bg-glass-3 rounded-lg p-3 border border-divider">
            <p className="text-xs text-content-muted">Total ventas (neto)</p>
            <p className="text-base font-semibold text-content mt-0.5">{fmtCur(summary.total_sales)}</p>
          </div>
          <div className="bg-glass-3 rounded-lg p-3 border border-divider">
            <p className="text-xs text-content-muted">Tasa efectiva</p>
            <p className="text-base font-semibold text-primary mt-0.5">{summary.effective_rate}%</p>
          </div>
          <div className="bg-glass-3 rounded-lg p-3 border border-divider">
            <p className="text-xs text-content-muted">Pedidos / Líneas</p>
            <p className="text-base font-semibold text-content mt-0.5">
              {summary.orders_count} / {summary.lines_count}
            </p>
          </div>
        </div>
      )}

      {isLoading ? <LoadingSkeleton /> : error ? (
        <p className="text-sm text-danger-solid">Error cargando reporte</p>
      ) : (
        <div>
          <DataTable<CommissionReportRow>
            columns={[
              { key: 'order_number', header: 'Pedido', sortable: true,
                render: r => (
                  <>
                    <p className="font-medium text-content">{r.order_number}</p>
                    <p className="text-xs text-content-subtle">{r.order_date}</p>
                  </>
                ) },
              { key: 'seller_name', header: 'Vendedor', sortable: true,
                render: r => <span className="text-content-muted">{r.seller_name}</span> },
              { key: 'sku', header: 'SKU', sortable: true,
                render: r => <span className="font-mono text-xs text-content-muted">{r.sku}</span> },
              { key: 'commission_base_label', header: 'Base', sortable: true,
                render: r => (
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                    r.commission_base === 'margin'
                      ? 'bg-info-surface-strong text-info-content'
                      : 'bg-surface-muted text-content-muted'
                  }`}>
                    {r.commission_base_label}
                  </span>
                ) },
              // La columna del margen solo aparece cuando alguna línea comisiona
              // sobre margen: mostrarla llena de «—» sugiere un dato faltante.
              ...(hasMarginLines ? [{
                key: 'margin_amount', header: 'Margen empresa', numeric: true, sortable: true,
                sortValue: (r: CommissionReportRow) => (r.commission_base === 'margin' ? Number(r.margin_amount) : null),
                render: (r: CommissionReportRow) => (
                  <span className="text-content-muted">
                    {r.commission_base === 'margin' ? fmtCur(r.margin_amount) : '—'}
                  </span>
                ),
              }] : []),
              { key: 'line_net', header: 'Neto línea', numeric: true, sortable: true,
                sortValue: r => Number(r.line_net),
                render: r => <span className="text-content-muted">{fmtCur(r.line_net)}</span> },
              // El título explica por qué esta línea cobró esa tasa: sin esto,
              // dos vendedores con % distinto sobre el mismo producto parecen
              // un error del sistema.
              { key: 'commission_rate', header: 'Tasa', numeric: true, sortable: true,
                sortValue: r => Number(r.commission_rate),
                render: r => (
                  <span className="text-content-muted" title={r.commission_source_label}>
                    {r.commission_rate}%
                    {r.commission_source === 'rep_product' && (
                      <span className="ml-1 text-xs text-primary" title="Trato específico de este vendedor para este producto">•</span>
                    )}
                  </span>
                ) },
              { key: 'commission_amount', header: 'Comisión', numeric: true, sortable: true,
                sortValue: r => Number(r.commission_amount),
                render: r => <span className="font-semibold text-primary">{fmtCur(r.commission_amount)}</span> },
            ]}
            rows={data?.data ?? []}
            rowKey={(_, i) => i}
            surface={false}
            empty={<EmptyState title="Sin comisiones en el período seleccionado." size="sm" />}
            caption="Comisiones devengadas, línea por línea"
          />
        </div>
      )}
    </div>
  )
}

// ─── Módulo requerido por reporte (el usuario debe tener view en ese módulo) ───

const TAB_REQUIRED_MODULE: Record<string, string> = {
  'sales':        'sales.documents',
  'ar-aging':     'receivables',
  'purchases':    'purchases.invoices',
  'ap-aging':     'payables',
  'factoring':    'treasury.operations',
  'products':     'sales.products',
  'customers':    'sales.customers',
  'cost-centers': 'accounting.cost_centers',
  'f29':          'accounting.vouchers',
  'f22':          'accounting.vouchers',
  'commissions':  'sales.commissions',
  'contracts-by-month': 'sales.contracts',
}

// Reportes que solo aplican a tenants de un país específico.
const TAB_REQUIRED_COUNTRY: Record<string, string> = {
  'f29': 'CL',
  'f22': 'CL',
}

// Reportes con panel de ayuda contextual (clave en helpContent.ts).
const TAB_HELP_KEY: Record<string, string> = {
  'f29': 'reports.f29',
  'f22': 'reports.f22',
}

// ─── Tabs config ──────────────────────────────────────────────────────────────

const TABS = [
  { id: 'sales', label: 'Ventas', icon: FileText, component: SalesTab,
    description: 'Resumen de documentos emitidos agrupados por estado y tipo, con totales de subtotal, impuesto y monto total.' },
  { id: 'ar-aging', label: 'AR-Aging', icon: TrendingDown, component: ARAgingTab,
    description: 'Antigüedad de cuentas por cobrar: documentos pendientes de pago clasificados por tramos de vencimiento.' },
  { id: 'purchases', label: 'Compras', icon: ShoppingBag, component: PurchasesTab,
    description: 'Resumen de facturas de proveedores agrupadas por proveedor y estado, con totales de gasto.' },
  { id: 'ap-aging', label: 'AP-Aging', icon: AlertTriangle, component: APAgingTab,
    description: 'Antigüedad de cuentas por pagar: facturas de proveedores pendientes clasificadas por tramos de vencimiento.' },
  { id: 'factoring', label: 'Factoring', icon: Landmark, component: FactoringTab,
    description: 'Operaciones de factoring realizadas: montos cedidos, estado de cada operación y entidad financiera.' },
  { id: 'products', label: 'Ventas x Producto', icon: Package, component: SalesByProductTab,
    description: 'Ranking de productos vendidos por cantidad y revenue, ideal para identificar los productos estrella.' },
  { id: 'customers', label: 'Clientes', icon: Users, component: CustomerAnalysisTab,
    description: 'Ranking de clientes por revenue total, cantidad de documentos y fecha del último documento emitido.' },
  { id: 'cost-centers', label: 'Centros de Costo', icon: Building2, component: CostCenterTab,
    description: 'Ingresos, gastos y margen neto por cada centro de costo, para evaluar la rentabilidad por unidad de negocio.' },
  { id: 'f29', label: 'F29', icon: Receipt, component: F29Tab,
    description: 'Formulario 29 (Chile): IVA débito/crédito, retenciones y PPM con códigos SII por línea, total a pagar y PDF descargable.' },
  { id: 'f22', label: 'F22', icon: Receipt, component: F22Tab,
    description: 'Formulario 22 (Chile): estimación referencial del impuesto a la renta según régimen tributario, con PPM imputado como crédito.' },
  { id: 'commissions', label: 'Comisiones', icon: TrendingUp, component: CommissionReportTab,
    description: 'Detalle de comisiones por pedido y producto con desglose por base de cálculo (total o margen). Cada vendedor ve solo sus propias comisiones.' },
  { id: 'contracts-by-month', label: 'Contratos por mes', icon: CalendarRange, component: ContractsByMonthReport,
    description: 'Contratos vigentes en cada mes del año, con su valor pactado y total mensual. Filtra por vendedor y por cualquier campo personalizado del contrato.' },
] as const

// ─── Report Card Colors ──────────────────────────────────────────────────────

const CARD_COLORS: Record<string, { bg: string; iconBg: string; iconText: string; border: string }> = {
  sales:        { bg: 'hover:bg-primary-50/50',   iconBg: 'bg-primary-100',    iconText: 'text-primary',    border: 'hover:border-primary-200' },
  'ar-aging':   { bg: 'hover:bg-amber-50/50',  iconBg: 'bg-warning-surface-strong',   iconText: 'text-warning-content-soft',   border: 'hover:border-warning-border' },
  purchases:    { bg: 'hover:bg-orange-50/50',  iconBg: 'bg-orange-100 dark:bg-orange-900/40',  iconText: 'text-orange-600 dark:text-orange-300',  border: 'hover:border-orange-200 dark:hover:border-orange-800' },
  'ap-aging':   { bg: 'hover:bg-red-50/50',     iconBg: 'bg-danger-surface-strong',     iconText: 'text-danger-content-soft',     border: 'hover:border-danger-border' },
  factoring:    { bg: 'hover:bg-teal-50/50',    iconBg: 'bg-teal-100 dark:bg-teal-900/40',    iconText: 'text-teal-600 dark:text-teal-300',    border: 'hover:border-teal-200 dark:hover:border-teal-800' },
  products:     { bg: 'hover:bg-purple-50/50',  iconBg: 'bg-purple-100 dark:bg-purple-900/40',  iconText: 'text-purple-600 dark:text-purple-300',  border: 'hover:border-purple-200 dark:hover:border-purple-800' },
  customers:    { bg: 'hover:bg-cyan-50/50',    iconBg: 'bg-cyan-100 dark:bg-cyan-900/40',    iconText: 'text-cyan-600 dark:text-cyan-300',    border: 'hover:border-cyan-200 dark:hover:border-cyan-800' },
  'cost-centers': { bg: 'hover:bg-emerald-50/50', iconBg: 'bg-success-surface-strong',   iconText: 'text-success-content-soft',   border: 'hover:border-success-border' },
  f29:          { bg: 'hover:bg-violet-50/50',  iconBg: 'bg-info-surface-strong',  iconText: 'text-info-content-soft',  border: 'hover:border-info-border' },
  'contracts-by-month': { bg: 'hover:bg-primary-50/50', iconBg: 'bg-primary-100', iconText: 'text-primary', border: 'hover:border-primary-200' },
  commissions:  { bg: 'hover:bg-yellow-50/50', iconBg: 'bg-yellow-100 dark:bg-yellow-900/40',  iconText: 'text-yellow-600 dark:text-yellow-300',  border: 'hover:border-yellow-200 dark:hover:border-yellow-800' },
}

// ─── Report Hub (landing page) ───────────────────────────────────────────────

function ReportHub() {
  const navigate = useNavigate()
  const { isModuleEnabled, can } = usePermission()
  const tenantCountry = useAuthStore(s => s.activeTenant?.country)
  const visibleTabs = TABS.filter(t => {
    if (t.id === 'commissions' && !isModuleEnabled('commissions')) return false
    const requiredCountry = TAB_REQUIRED_COUNTRY[t.id]
    if (requiredCountry && tenantCountry !== requiredCountry) return false
    const requiredMod = TAB_REQUIRED_MODULE[t.id]
    return !requiredMod || can(requiredMod, 'view')
  })

  return (
    <>
      <div data-tour="reports-hub" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleTabs.map(tab => {
          const Icon = tab.icon
          const colors = CARD_COLORS[tab.id] || CARD_COLORS.sales
          return (
            <GlassPanel
              as="button"
              key={tab.id}
              onClick={() => navigate(`/reports/${tab.id}`)}
              className={`group text-left w-full transition-all duration-200
                ${colors.bg} ${colors.border} hover:shadow-[var(--shadow-floating)]`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl ${colors.iconBg} flex items-center justify-center shrink-0 transition-transform group-hover:scale-110`}>
                  <Icon size={20} className={colors.iconText} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-content mb-1">{tab.label}</h3>
                  <p className="text-xs text-content-muted leading-relaxed line-clamp-2">{tab.description}</p>
                </div>
              </div>
            </GlassPanel>
          )
        })}
      </div>
    </>
  )
}

// ─── Report Detail Page (individual report) ──────────────────────────────────

export function ReportDetailPage() {
  const { reportId } = useParams<{ reportId: string }>()
  const navigate = useNavigate()
  const { isModuleEnabled, can } = usePermission()
  const tenantCountry = useAuthStore(s => s.activeTenant?.country)
  const [helpOpen, setHelpOpen] = useState(false)
  const tab = TABS.find(t => {
    if (t.id !== reportId) return false
    if (t.id === 'commissions' && !isModuleEnabled('commissions')) return false
    const requiredCountry = TAB_REQUIRED_COUNTRY[t.id]
    if (requiredCountry && tenantCountry !== requiredCountry) return false
    const requiredMod = TAB_REQUIRED_MODULE[t.id]
    return !requiredMod || can(requiredMod, 'view')
  })

  if (!tab) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <p className="text-content-muted">Reporte no encontrado.</p>
        <button onClick={() => navigate('/reports')} className="text-primary text-sm mt-2 hover:underline">
          Volver al Centro de Reportes
        </button>
      </div>
    )
  }

  const Icon = tab.icon
  const Component = tab.component
  const help = TAB_HELP_KEY[tab.id] ? HELP[TAB_HELP_KEY[tab.id]] : undefined

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header with back link */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/reports')}
          className="text-xs text-content-subtle hover:text-primary transition-colors mb-2 flex items-center gap-1"
        >
          ← Centro de Reportes
        </button>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${(CARD_COLORS[tab.id] || CARD_COLORS.sales).iconBg} flex items-center justify-center`}>
            <Icon size={18} className={(CARD_COLORS[tab.id] || CARD_COLORS.sales).iconText} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-content">{tab.label}</h1>
              {help && (
                <button
                  onClick={() => setHelpOpen(true)}
                  className="p-1 rounded-full text-content-subtle hover:text-primary hover:bg-primary-50 transition-colors"
                  title="Más información sobre este reporte"
                  aria-label="Ayuda sobre este reporte"
                >
                  <HelpCircle size={16} />
                </button>
              )}
            </div>
            <p className="text-sm text-content-muted mt-0.5">{tab.description}</p>
          </div>
        </div>
      </div>

      {/* Report content */}
      <GlassPanel>
        <Component />
      </GlassPanel>

      {help && (
        <HelpPanel
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          title={help.title}
          sections={help.sections}
        />
      )}
    </div>
  )
}

// ─── Main Page (Hub) ─────────────────────────────────────────────────────────

export function ReportsCenterPage() {
  const navigate = useNavigate()
  return (
    <div data-tour="reports-page" className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-content">Centro de Reportes</h1>
        <p className="text-content-muted text-sm mt-1">Selecciona un reporte para ver datos detallados y exportar a Excel</p>
      </div>

      {/* Reportes personalizados — destacado */}
      <button
        data-tour="reports-custom"
        onClick={() => navigate('/reports/custom')}
        className="w-full text-left bg-gradient-to-r from-primary-50 to-surface rounded-xl border border-primary-200 dark:border-primary-800 p-5 mb-5 hover:shadow-md transition-shadow group"
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 transition-transform group-hover:scale-110">
            <FileText size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-content mb-1 flex items-center gap-2">
              Reportes Personalizados
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono uppercase">Nuevo</span>
            </h3>
            <p className="text-xs text-content-muted leading-relaxed">
              Elige la entidad, los campos y filtros que necesitas. Exporta a Excel (XLSX) o CSV.
            </p>
          </div>
          <ChevronRight size={18} className="text-content-subtle shrink-0" />
        </div>
      </button>

      <ReportHub />
    </div>
  )
}
